import React, { useState, useEffect } from 'react';
import { Eye, Trash2, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import api from '../services/api';
import toast from 'react-hot-toast';
import LoadingSpinner from '../components/LoadingSpinner';
import { useAuth } from '../contexts/AuthContext';
import { useTheme, UI_THEMES } from '../contexts/ThemeContext';

const InvoicesTrash = () => {
  const { user } = useAuth();
  const { theme } = useTheme();
  const isModern = theme === UI_THEMES.MODERN;
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [pageSize] = useState(20);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchQuery(searchQuery), 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    if (!user?.canDeleteInvoices) {
      toast.error('Brak uprawnień do przeglądania kosza');
      return;
    }
    fetchDeletedInvoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, debouncedSearchQuery, dateFrom, dateTo, user]);

  const fetchDeletedInvoices = async () => {
    if (!user?.canDeleteInvoices) return;

    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: currentPage.toString(),
        size: pageSize.toString(),
      });

      if (debouncedSearchQuery) {
        params.append('q', debouncedSearchQuery);
      }
      if (dateFrom) {
        params.append('dateFrom', dateFrom);
      }
      if (dateTo) {
        params.append('dateTo', dateTo);
      }

      const response = await api.get(`/invoices/deleted?${params.toString()}`);
      setInvoices(response.data.content || []);
      setTotalPages(response.data.totalPages || 0);
      setTotalElements(response.data.totalElements || 0);
    } catch (error) {
      console.error('Błąd podczas pobierania usuniętych faktur:', error);
      toast.error('Nie udało się pobrać usuniętych faktur');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('pl-PL');
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('pl-PL');
  };

  const handleViewPdf = (invoice) => {
    if (!invoice.id) return;
    if (!invoice.filePath && !invoice.ksefReference) {
      toast.error('Brak załącznika dla tej faktury');
      return;
    }
    const previewUrl = `/app/invoices/${invoice.id}/preview`;
    window.open(previewUrl, '_blank', 'width=1200,height=800');
  };

  const clearFilters = () => {
    setSearchQuery('');
    setDateFrom('');
    setDateTo('');
    setCurrentPage(0);
  };

  if (!user?.canDeleteInvoices) {
    return (
      <div className="card w-full min-w-0 max-w-full border-amber-100/80 shadow-md shadow-amber-900/5">
        <div className="card-body flex flex-col items-center justify-center py-16">
          <div className="rounded-2xl bg-amber-100 p-4 mb-4">
            <Trash2 className="h-12 w-12 text-amber-600" />
          </div>
          <p className="text-gray-600 text-center">Brak uprawnień do przeglądania kosza</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`card w-full min-w-0 max-w-full border-primary-100/60 shadow-md shadow-primary-900/5 overflow-hidden ${isModern ? 'crm-modern-invoices' : ''}`}>
      <div className={`card-header border-b border-primary-100/50 ${isModern ? 'bg-white' : 'bg-gradient-to-r from-primary-50 via-white to-amber-50/40'}`}>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="hidden sm:flex shrink-0 rounded-xl bg-gradient-to-br from-primary-500 to-primary-700 p-2.5 shadow-sm shadow-primary-600/25">
              <Trash2 className="h-6 w-6 text-white" aria-hidden />
            </div>
            <div>
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 tracking-tight">Kosz</h2>
              <p className="mt-0.5 text-xs sm:text-sm text-primary-800/70">Przeglądaj usunięte faktury</p>
            </div>
          </div>
          {!loading && totalElements > 0 && (
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-semibold bg-amber-100 text-amber-900 border border-amber-200/80 shadow-sm shrink-0">
              <span className="tabular-nums">{totalElements}</span>
              <span className="text-amber-800/90 font-medium">
                {totalElements === 1 ? 'faktura' : totalElements < 5 ? 'faktury' : 'faktur'}
              </span>
            </span>
          )}
        </div>
      </div>

      <div className="card-body border-b border-primary-100/40 bg-gradient-to-b from-slate-50/90 to-white py-4">
        <div className="flex flex-col xl:flex-row xl:flex-wrap gap-3 xl:items-end">
          <div className="relative flex-1 min-w-0 xl:min-w-[220px]">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-primary-500/70" />
            </div>
            <input
              type="text"
              placeholder="Szukaj po numerze, dostawcy, NIP..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(0);
              }}
              className="block w-full pl-10 pr-3 py-2 border border-primary-200/60 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-400/40 focus:border-primary-500 sm:text-sm shadow-sm"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-none xl:flex gap-3 w-full xl:w-auto">
            <div className="w-full sm:min-w-[140px] xl:w-40">
              <label className="block text-xs font-semibold text-primary-900/70 mb-1">Data od</label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => {
                  setDateFrom(e.target.value);
                  setCurrentPage(0);
                }}
                className="input text-sm py-2 border-primary-200/50 shadow-sm"
              />
            </div>
            <div className="w-full sm:min-w-[140px] xl:w-40">
              <label className="block text-xs font-semibold text-primary-900/70 mb-1">Data do</label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => {
                  setDateTo(e.target.value);
                  setCurrentPage(0);
                }}
                className="input text-sm py-2 border-primary-200/50 shadow-sm"
              />
            </div>
          </div>
          <div className="w-full xl:w-auto xl:shrink-0">
            <button type="button" onClick={clearFilters} className="btn-secondary w-full xl:w-auto whitespace-nowrap">
              Wyczyść filtry
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="card-body flex items-center justify-center py-16">
          <LoadingSpinner size="lg" />
        </div>
      ) : invoices.length === 0 ? (
        <div className="card-body flex flex-col items-center justify-center py-16 text-center bg-gradient-to-b from-slate-50/50 to-white">
          <div className="rounded-2xl bg-primary-100 p-4 mb-3">
            <Trash2 className="h-10 w-10 text-primary-500" />
          </div>
          <p className="text-gray-800 font-semibold">Brak usuniętych faktur</p>
          <p className="text-sm text-primary-900/60 mt-1">Zmień filtry lub sprawdź później.</p>
        </div>
      ) : (
        <>
          <div className="w-full min-w-0 overflow-x-auto overscroll-x-contain bg-slate-50/40">
            <table className="w-full min-w-[1024px] table-fixed border-collapse">
              <colgroup>
                <col className="w-[14%]" />
                <col className="w-[18%]" />
                <col className="w-[8%]" />
                <col className="w-[10%]" />
                <col className="w-[12%]" />
                <col className="w-[13%]" />
                <col className="w-[20%]" />
                <col className="w-[5%]" />
              </colgroup>
              <thead>
                <tr className="bg-gradient-to-r from-primary-600 to-primary-700 text-white shadow-sm">
                  <th className="px-2 sm:px-3 py-3 text-left text-[10px] sm:text-xs font-bold uppercase tracking-wide border-r border-white/15 last:border-r-0">
                    Numer faktury
                  </th>
                  <th className="px-2 sm:px-3 py-3 text-left text-[10px] sm:text-xs font-bold uppercase tracking-wide border-r border-white/15 last:border-r-0">
                    Dostawca
                  </th>
                  <th className="px-2 sm:px-3 py-3 text-left text-[10px] sm:text-xs font-bold uppercase tracking-wide border-r border-white/15 whitespace-nowrap last:border-r-0">
                    Data wyst.
                  </th>
                  <th className="px-2 sm:px-3 py-3 text-left text-[10px] sm:text-xs font-bold uppercase tracking-wide border-r border-white/15 whitespace-nowrap last:border-r-0">
                    Kwota brutto
                  </th>
                  <th className="px-2 sm:px-3 py-3 text-left text-[10px] sm:text-xs font-bold uppercase tracking-wide border-r border-white/15 last:border-r-0">
                    Usunięta przez
                  </th>
                  <th className="px-2 sm:px-3 py-3 text-left text-[10px] sm:text-xs font-bold uppercase tracking-wide border-r border-white/15 whitespace-nowrap last:border-r-0">
                    Data usunięcia
                  </th>
                  <th className="px-2 sm:px-3 py-3 text-left text-[10px] sm:text-xs font-bold uppercase tracking-wide border-r border-white/15 last:border-r-0">
                    Powód
                  </th>
                  <th className="px-2 sm:px-3 py-3 text-center text-[10px] sm:text-xs font-bold uppercase tracking-wide w-14 bg-primary-800/30">
                    Akcje
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white">
                {invoices.map((invoice, index) => (
                  <tr
                    key={invoice.id}
                    className={`border-b border-gray-200/90 transition-colors duration-150 ${
                      index % 2 === 0 ? 'bg-white' : 'bg-slate-50/80'
                    } hover:bg-primary-50/90`}
                  >
                    <td
                      className="px-2 sm:px-3 py-2.5 align-middle border-r border-gray-100 overflow-hidden"
                      style={{ width: '14%', maxWidth: '14%' }}
                    >
                      <div className="flex items-stretch gap-1.5 w-full max-w-full min-w-0 overflow-hidden">
                        <span className="w-1 shrink-0 rounded-full bg-primary-500 self-center min-h-[1.25rem]" aria-hidden />
                        <span
                          className="min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-xs sm:text-sm font-medium text-gray-900"
                          title={invoice.number}
                        >
                          {invoice.number}
                        </span>
                      </div>
                    </td>
                    <td
                      className="px-2 sm:px-3 py-2.5 align-middle border-r border-gray-100 overflow-hidden"
                      style={{ width: '18%', maxWidth: '18%' }}
                    >
                      <span
                        className="text-xs sm:text-sm text-slate-700 font-normal block w-full max-w-full min-w-0 overflow-hidden text-ellipsis whitespace-nowrap"
                        title={invoice.supplierName || ''}
                      >
                        {invoice.supplierName || '-'}
                      </span>
                    </td>
                    <td className="px-2 sm:px-3 py-2.5 align-middle border-r border-gray-100 whitespace-nowrap">
                      <span className="text-xs sm:text-sm text-slate-600 tabular-nums">{formatDate(invoice.issueDate)}</span>
                    </td>
                    <td className="px-2 sm:px-3 py-2.5 align-middle border-r border-gray-100 whitespace-nowrap text-right">
                      <span className="text-xs sm:text-sm font-medium text-emerald-800 tabular-nums inline-block w-full text-right">
                        {invoice.amountGross != null && invoice.amountGross !== ''
                          ? `${Number(invoice.amountGross).toFixed(2)} ${invoice.currency || 'PLN'}`
                          : '-'}
                      </span>
                    </td>
                    <td className="px-2 sm:px-3 py-2.5 align-middle border-r border-gray-100 max-w-0 min-w-0 overflow-hidden">
                      <span className="text-xs sm:text-sm text-slate-600 truncate block min-w-0" title={invoice.deletedByName || ''}>
                        {invoice.deletedByName || '-'}
                      </span>
                    </td>
                    <td className="px-2 sm:px-3 py-2.5 align-middle border-r border-gray-100 max-w-0 min-w-0 overflow-hidden">
                      <span
                        className="text-[11px] sm:text-xs text-slate-600 leading-snug truncate block min-w-0"
                        title={formatDateTime(invoice.deletedAt)}
                      >
                        {formatDateTime(invoice.deletedAt)}
                      </span>
                    </td>
                    <td className="px-2 sm:px-3 py-2.5 align-middle border-r border-gray-100 max-w-0 min-w-0 overflow-hidden bg-amber-50/40">
                      <span
                        className="text-xs sm:text-sm text-amber-950/80 truncate block min-w-0"
                        title={invoice.deletionReason || ''}
                      >
                        {invoice.deletionReason || '-'}
                      </span>
                    </td>
                    <td className="px-2 sm:px-3 py-2.5 align-middle text-center bg-slate-50/50">
                      <button
                        type="button"
                        onClick={() => handleViewPdf(invoice)}
                        className="inline-flex p-2 rounded-lg text-primary-600 bg-white border border-primary-200/80 shadow-sm hover:bg-primary-600 hover:text-white hover:border-primary-600 transition-colors"
                        title="Podgląd faktury"
                      >
                        <Eye className="h-4 w-4 sm:h-5 sm:w-5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="px-4 sm:px-5 md:px-6 py-3 sm:py-4 border-t border-primary-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 bg-gradient-to-r from-primary-50/80 to-slate-50/90">
              <p className="text-xs sm:text-sm text-gray-600">
                Wyświetlanie{' '}
                <span className="font-medium text-gray-800">
                  {currentPage * pageSize + 1} – {Math.min((currentPage + 1) * pageSize, totalElements)}
                </span>{' '}
                z {totalElements}
              </p>
              <div className="flex items-center gap-2 justify-end">
                <button
                  type="button"
                  onClick={() => setCurrentPage((prev) => Math.max(0, prev - 1))}
                  disabled={currentPage === 0}
                  className="p-2 border border-primary-200 rounded-lg bg-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-primary-100 hover:border-primary-300 transition-colors"
                  aria-label="Poprzednia strona"
                >
                  <ChevronLeft className="h-5 w-5 text-primary-700" />
                </button>
                <span className="px-2 text-xs sm:text-sm font-medium text-primary-900 tabular-nums whitespace-nowrap">
                  {currentPage + 1} / {totalPages}
                </span>
                <button
                  type="button"
                  onClick={() => setCurrentPage((prev) => Math.min(totalPages - 1, prev + 1))}
                  disabled={currentPage >= totalPages - 1}
                  className="p-2 border border-primary-200 rounded-lg bg-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-primary-100 hover:border-primary-300 transition-colors"
                  aria-label="Następna strona"
                >
                  <ChevronRight className="h-5 w-5 text-primary-700" />
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default InvoicesTrash;
